import type { PortableTextBlock } from '@portabletext/types'
import type { ImageAsset, Slug } from '@sanity/types'
import groq from 'groq'
import { type SanityClient } from 'next-sanity'
export const homepageQuery = groq`*[_type == "homepage"][0]`

export async function getHomepage(client: SanityClient): Promise<Homepage> {
  return await client.fetch(homepageQuery)
}

export interface BasicSection {
  title: PortableTextBlock[]
  body: PortableTextBlock[]
  primaryCTA: CTA
  secondaryCTA: CTA
}

export interface Homepage {
  _type: 'homepage'
  hero: BasicSection
  features: {
    title: PortableTextBlock[]
    ourNodes: {
      body: string[]
      providers: {
        image: ImageAsset
        name: string
      }[]
    }
    deployment: {
      text: PortableTextBlock[]
      image: ImageAsset
    }[]
  }
  ourServices: {
    title: PortableTextBlock[]
    body: PortableTextBlock[]
    host: SimpleCard
    rent: SimpleCard
    pricingPlayground: {
      heading: PortableTextBlock[]
      body: PortableTextBlock[]
      savings: {
        savingsPerMonth: number
        aws: {
          totalCost: number
          savingPerHour: number
        }
        nodeAI: {
          totalCost: number
          savingPerHour: number
        }
      }
      cudaCors: {
        aws: number
        nodeAI: number
      }
      teraflops: {
        aws: number
        nodeAI: number
      }
      memory: {
        aws: string
        nodeAI: string
      }
    }
    paymentDescription: {
      heading: string
      body: string
    }[]
  }
  howItWorks: {
    title: PortableTextBlock[]
    streamlinePlatform: {
      heading: string
      body: PortableTextBlock[]
    }
    encryption: {
      block: PortableTextBlock[]
    }[]
  }
  gpuPower: {
    title: PortableTextBlock[]
    body: PortableTextBlock[]
    cardOne: {
      kicker: string
      heading: string
    }
    cardTwo: {
      kicker: string
      heading: string
    }
    cardThree: {
      heading: string
      kicker: string
    }
    cardFour: {
      kicker: string
      heading: string
      cta: CTA
      lead: string
      body: PortableTextBlock[]
      features: {
        body: PortableTextBlock[]
      }[]
    }
  }
  launch: BasicSection
}

export interface CTA {
  text: string
  url: string
}

export interface SimpleCard {
  _type: 'simpleCard'
  heading: string
  body: PortableTextBlock[]
  primaryCTA: CTA
  secondaryCTA: CTA
}

export const navigationQuery = groq`*[_type == "navigation"][0]`

export async function getNavigation(client: SanityClient): Promise<Navigation> {
  return await client.fetch(navigationQuery)
}

export interface Navigation {
  _type: 'navigation'
  logo: ImageAsset
  main: NavigationItem[]
  mainCta: CTA
  footer: {
    heading: PortableTextBlock[]
    cta: CTA
    secondaryMenu: FooterItem[]
  }
}

export interface FooterItem {
  heading: string
  subitems: CTA[]
}

export interface NavigationItem {
  page: CTA
  subitems: CTA[]
}

export const hostANodeQuery = groq`*[_type == "secondaryPage"][0]`

export async function getHostANode(client: SanityClient): Promise<HostANode> {
  return await client.fetch(hostANodeQuery)
}

export const rentANodeQuery = groq`*[_type == "secondaryPage"][1]`

export async function getRentANode(client: SanityClient): Promise<RentANode> {
  return await client.fetch(rentANodeQuery)
}

export const faqsQuery = groq`*[_type == "faqs"][0]{
  title,
  introduction,
  categories[]{
    title,
    questions[]{
      question,
      answer
    }
  }
}`

export async function getFAQs(client: SanityClient): Promise<FAQs> {
  return await client.fetch(faqsQuery)
}

export interface SecondaryPage {
  _type: 'secondaryPage'
  hero: {
    title: PortableTextBlock[]
    body: PortableTextBlock[]
  }
  overviewCards: Array<{
    kicker: string
    title: PortableTextBlock[]
    body: PortableTextBlock[]
    primaryCTA: CTA
    secondaryCTA: CTA
    image: ImageAsset
  }>
  callOut: {
    title: PortableTextBlock[]
    body: PortableTextBlock[]
    primaryCTA: CTA
    secondaryCTA: CTA
  }
  howItWorks: {
    title: PortableTextBlock[]
    body: PortableTextBlock[]
    items: Array<{
      title: string
      body: string
      image: ImageAsset
    }>
  }
  summary: {
    title: PortableTextBlock[]
    body: PortableTextBlock[]
    items: Array<{
      icon: ImageAsset
      title: string
      body: string
    }>
  }
  cta: BasicSection
}

export interface FAQs {
  _type: 'faqsPage'
  title: string
  introduction?: PortableTextBlock[]
  categories: Array<{
    title: string
    questions: Array<{
      question: string
      answer: PortableTextBlock[]
    }>
  }>
}

export interface HostANode extends SecondaryPage {}
export interface RentANode extends SecondaryPage {}

export const pricingPageQuery = groq`*[_type == "pricingPage"][0]{
  ...,
  calculator {
    ...,
    nodeaiLogo {
      ...,
      asset-> {
        url
      }
    },
    amazonLogo {
      ...,
      asset-> {
        url
      }
    },
    gcpLogo {
      ...,
      asset-> {
        url
      }
    },
    azureLogo {
      ...,
      asset-> {
        url
      }
    }
  }
}`

export async function getPricingPage(
  client: SanityClient,
): Promise<PricingPage> {
  return await client.fetch(pricingPageQuery)
}
export interface PricingPage {
  _type: 'pricingPage'
  title: PortableTextBlock[]
  options: Array<{
    productImage: ImageAsset
    productType: string
    model: string
    supply: number
    pricePerHour: number
  }>
  calculator: {
    title: PortableTextBlock[]
    description: PortableTextBlock[]
    nodeaiLogo: {
      asset: {
        url: string;
      }
    }
    amazonLogo: {
      asset: {
        url: string;
      }
    }
    gcpLogo: {
      asset: {
        url: string;
      }
    }
    azureLogo: {
      asset: {
        url: string;
      }
    }
  }
}

export const gpuTokenPageQuery = groq`*[_type == "gpuTokenPage"][0]`

export async function getGPUTokenPage(
  client: SanityClient,
): Promise<GPUTokenPage> {
  return await client.fetch(gpuTokenPageQuery)
}
export interface GPUTokenPage {
  _type: 'gpuTokenPage'
  title: PortableTextBlock[]
  liquidity: number
  CEX: number
  contractAddress: string
  details: Array<{
    title: string
    body: PortableTextBlock[]
  }>
  poweringNodeAI: {
    eyebrow: string
    title: string
    description: string
    features: Array<{
      title: string
      description: string
    }>
  }
  tradingVenues: {
    eyebrow: string
    title: string
    description: string
    cexCard: {
      title: string
      description: string
      exchanges: Array<{
        alt: string
      }>
    }
    dexCard: {
      title: string
      description: string
      exchanges: Array<{
        alt: string
      }>
    }
  }
  keyFeatures: {
    eyebrow: string
    title: string
    description: string
    features: Array<{
      title: string
      description: string
    }>
  }
}
export const showcaseWithCategoriesQuery = groq`
  {
    "items": *[_type == "showcase"] {
      ...,
      "body": body,
      categories[]->,
    } | order(date desc),
    "categories": *[_type == "category" && categoryType == "showcase" && count(*[_type == "showcase" && references(^._id)]) > 0]
  }
`

export const toolsWithCategoriesQuery = groq`
  {
    "items": *[_type == "tools"] {
      ...,
      "body": body,
      categories[]->,
    } | order(date desc),
    "categories": *[_type == "category" && categoryType == "tools" && count(*[_type == "tools" && references(^._id)]) > 0]
  }
`

export const deployedWithCategoriesQuery = groq`
  {
    "items": *[_type == "deployed"] {
      ...,
      "body": body,
      categories[]->,
    } | order(date desc),
    "categories": *[_type == "category" && categoryType == "deployed" && count(*[_type == "deployed" && references(^._id)]) > 0]
  }
`

export const providersWithCategoriesQuery = groq`
  {
    "items": *[_type == "providers"] {
      ...,
      "body": body,
      categories[]->,
    } | order(date desc),
    "categories": *[_type == "category" && categoryType == "providers" && count(*[_type == "providers" && references(^._id)]) > 0]
  }
`

export async function getShowcaseWithCategories(
  client: SanityClient,
): Promise<{ items: Showcase[]; categories: Category[] }> {
  return await client.fetch(showcaseWithCategoriesQuery)
}

export async function getToolsWithCategories(
  client: SanityClient,
): Promise<{ items: Tools[]; categories: Category[] }> {
  return await client.fetch(toolsWithCategoriesQuery)
}

export async function getDeployedWithCategories(
  client: SanityClient,
): Promise<{ items: Deployed[]; categories: Category[] }> {
  return await client.fetch(deployedWithCategoriesQuery)
}

export async function getProvidersWithCategories(
  client: SanityClient,
): Promise<{ items: Providers[]; categories: Category[] }> {
  return await client.fetch(providersWithCategoriesQuery)
}

export async function getBlogList(client: SanityClient): Promise<Blog[]> {
  return await client.fetch(blogListQuery)
}

export async function getBlogBySlug(client: SanityClient, slug: string): Promise<Blog> {
  return await client.fetch(blogBySlugQuery, { slug })
}

export async function getBlogCategories(client: SanityClient): Promise<Category[]> {
  return await client.fetch(blogCategoriesQuery)
}

export const blogListQuery = groq`
  *[_type == "blog"] | order(publishedAt desc) {
    _id,
    title,
    slug,
    mainImage,
    publishedAt,
    excerpt,
    "category": category->title,
    "categorySlug": category->slug.current,
    minutesToRead
  }
`

export const blogBySlugQuery = groq`
  *[_type == "blog" && slug.current == $slug][0] {
    _id,
    title,
    mainImage {
      asset-> {
        url
      },
      alt
    },
    publishedAt,
    content,
    "category": category->title,
    "categorySlug": category->slug.current,
    minutesToRead
  }
`

export const blogCategoriesQuery = groq`
  *[_type == "blogCategory"] {
    _id,
    title,
    slug,
    description
  }
`



export const brandPageQuery = groq`*[_type == "brandPage"][0]{
  pageTitle,
  pageDescription,
  logos{
    sectionTitle,
    description,
    logoVariants[]{
      title,
      image,
      downloadFiles[]{
        _type,
        _key,
        asset->{url}
      }
    }
  },
  colorPalette[]{
    colorName,
    hexCode,
    description
  },
  brandGuidelines{
    _type,
    asset->{url}
  }
}`

export async function getBrandPage(
  client: SanityClient,
): Promise<BrandPage> {
  return await client.fetch(brandPageQuery)
}

export interface BrandPage {
  _type: 'brandPage'
  pageTitle: string
  pageDescription: string
  logos: {
    sectionTitle: string
    description?: string
    logoVariants: Array<{
      title: string
      image: ImageAsset
      downloadFiles?: Array<{
        _type: 'file'
        _key: string
        asset: {
          url: string
        }
      }>
    }>
  }
  colorPalette?: Array<{
    colorName: string
    hexCode: string
    description?: string
  }>
  typography?: {
    primaryFont: string
    secondaryFont?: string
    fontDownloads?: Array<{
      _type: 'file'
      asset: {
        url: string
      }
    }>
  }
  brandGuidelines?: {
    _type: 'file'
    asset: {
      url: string
    }
  }
}

export interface Providers {
  _type: 'providers'
  name: string
  url: string
  status: 'pending' | 'active' | 'inactive'
  uptime: number
  cpu: number
  cpuMax: number
  gpu: number
  gpuMax: number
  memory: string
  memoryMax: string
  activeLeases: number
  region: string
  categories: Category[]
  cloudmosUrl: string
  image: ImageAsset
}
export interface Deployed {
  _type: 'deployed'
  title: string
  image: ImageAsset
  body: string
  date: string
  categories: Category[]
  url: string
}
export interface Showcase {
  _type: 'showcase'
  title: string
  image: ImageAsset
  body: string
  date: string
  categories: Category[]
  url: string
}

export interface Tools {
  _type: 'tools'
  title: string
  image: ImageAsset
  body: string
  date: string
  categories: Category[]
  url: string
}

export interface Category {
  _type: 'category'
  _id: string
  name: string
}

export interface Blog {
  _type: 'blog'
  title: string
  slug: Slug
  mainImage: ImageAsset
  publishedAt: string
  excerpt: string
  category: string
  categorySlug: string
  minutesToRead: number
}

export const careersPageQuery = groq`
  *[_type == "careersPage"][0] {
    hero {
      title,
      body
    },
    benefits {
      title,
      items[] {
        title,
        description,
        icon
      }
    },
    jobListingsSection {
      title,
      description
    },
    culture {
      title,
      description
    }
  }
`

export const getCareersPage = async (client: SanityClient) => {
  return await client.fetch(careersPageQuery)
}


export const getJobListings = groq`
  *[_type == "jobListing"] {
    _id,
    title,
    slug,
    location,
    type,
    responsibilities,
    requirements,
    benefits,
    applicationUrl,
    applicationEmail,
    skills,
    description,
    department
  }
`

export interface JobListing {
  _id: string
  title: string
  slug: {
    current: string
  }
  location: string
  type: string
  salary?: string
  summary: string
  responsibilities: any[] // PortableText block
  requirements: any[] // PortableText block
  benefits?: any[] // PortableText block
  applicationUrl?: string
  applicationEmail?: string
  skills: string[]
  description: PortableTextBlock[]
  department: string
}

export interface CareersPage {
  _type: 'careersPage'
  hero: {
    title: PortableTextBlock[]
    body: PortableTextBlock[]
    image?: ImageAsset
  }
  mission: {
    title: string
    body: PortableTextBlock[]
  }
  benefits: {
    title: string
    items: Array<{
      title: string
      description: string
      icon?: ImageAsset
    }>
  }
  jobListingsSection: {
    title: string
    description?: PortableTextBlock[]
    featuredJobs?: JobListing[]
  }
  culture: {
    title: string
    description: PortableTextBlock[]
    images?: ImageAsset[]
  }
}
